import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";

interface Shift {
  id: string;
  scheduled_start: string;
  scheduled_end: string;
  claimed_first_name?: string | null;
  role: string | null;
  status: string;
  notes: string | null;
}

interface Overview {
  employee: { id: string; first_name: string; last_name: string };
  clocked_in: { id: string; punch_in_at: string; hours_elapsed: number } | null;
  hours_this_week: number;
  upcoming_shifts: Shift[];
}

interface TimeOffRequest {
  id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  status: "pending" | "approved" | "denied";
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function isToday(iso: string): boolean {
  return new Date(iso).toDateString() === new Date().toDateString();
}

export default function MySchedulePage() {
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [clockNote, setClockNote] = useState("");
  const [timeOff, setTimeOff] = useState<TimeOffRequest[]>([]);
  const [showAskOff, setShowAskOff] = useState(false);
  const [offStart, setOffStart] = useState("");
  const [offEnd, setOffEnd] = useState("");
  const [offReason, setOffReason] = useState("");
  const [askOffBusy, setAskOffBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/scheduling/my/overview");
      setOverview(res.data);
      setError("");
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || "Could not load your schedule");
    }
    try {
      const res = await api.get("/scheduling/my/time-off");
      setTimeOff(res.data);
    } catch {
      // time-off list is non-critical
    }
  }, []);

  const submitAskOff = async () => {
    if (!offStart || !offEnd) return;
    setAskOffBusy(true);
    try {
      await api.post("/scheduling/my/time-off", {
        start_date: offStart,
        end_date: offEnd,
        reason: offReason || undefined,
      });
      setShowAskOff(false);
      setOffStart("");
      setOffEnd("");
      setOffReason("");
      await load();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || "Could not send request");
    } finally {
      setAskOffBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const handleSwap = async (shiftId: string, action: "swap-request" | "swap-cancel") => {
    setBusy(true);
    try {
      await api.post(`/scheduling/my/shifts/${shiftId}/${action}`);
      await load();
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || "Swap update failed");
    }
    setBusy(false);
  };

  const handleClock = async (action: "clock-in" | "clock-out") => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/scheduling/my/${action}`, clockNote.trim() ? { note: clockNote.trim() } : {});
      setClockNote("");
      await load();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const clockedIn = !!overview?.clocked_in;

  return (
    <div className="min-h-screen" style={{ background: "var(--rbw-bg)" }}>
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between" style={{ background: "var(--rbw-navy)" }}>
        <div>
          <img src="/images/logo-secondary.jpg" alt="RBW" className="h-8 rounded" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-white">
            {user?.first_name}
          </span>
          <Link
            to="/scheduling"
            className="text-xs font-bold px-3 py-1.5 rounded-lg"
            style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}
          >
            Store Schedule
          </Link>
          <Link
            to="/upc-catchup"
            className="text-xs font-bold px-3 py-1.5 rounded-lg"
            style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}
          >
            Karen To-Do
          </Link>
          <Link
            to="/register"
            className="text-xs font-bold px-3 py-1.5 rounded-lg"
            style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
          >
            Register
          </Link>
          <button
            onClick={logout}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg"
            style={{ background: "rgba(255,255,255,0.12)", color: "#fff" }}
          >
            Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-5 space-y-5">
        {error && (
          <div
            className="px-4 py-3 rounded-xl text-sm font-medium flex items-center justify-between gap-3"
            style={{ background: "#FEE2E2", color: "var(--rbw-danger)", border: "1px solid #FECACA" }}
          >
            <span>{error}</span>
            <button
              onClick={load}
              className="text-xs font-bold px-3 py-1.5 rounded-lg shrink-0"
              style={{ background: "var(--rbw-danger)", color: "#fff" }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Time clock card */}
        <div
          className="rounded-2xl p-5 shadow-sm"
          style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg" style={{ color: "var(--rbw-navy)" }}>Time Clock</h2>
            <span className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
              {overview ? `${overview.hours_this_week.toFixed(2)} hrs this week` : ""}
            </span>
          </div>

          {clockedIn && overview?.clocked_in && (
            <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
              Clocked in at <strong>{overview.clocked_in.punch_in_at ? fmtTime(overview.clocked_in.punch_in_at) : "?"}</strong>
              {" "}({Number(overview.clocked_in.hours_elapsed ?? 0).toFixed(2)} hrs)
            </p>
          )}

          <input
            type="text"
            placeholder={`Note for Patrick (optional) — e.g. ${clockedIn ? "leaving early for appt" : "running late, traffic"}`}
            value={clockNote}
            onChange={(e) => setClockNote(e.target.value)}
            maxLength={500}
            className="w-full px-3 py-2.5 mb-3 rounded-xl outline-none text-sm"
            style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg)", color: "var(--rbw-navy)" }}
          />

          <button
            onClick={() => handleClock(clockedIn ? "clock-out" : "clock-in")}
            disabled={busy || !overview}
            className="w-full py-4 rounded-xl font-bold text-lg disabled:opacity-50 active:scale-[0.98]"
            style={
              clockedIn
                ? { background: "var(--rbw-danger)", color: "#fff" }
                : { background: "var(--rbw-gold)", color: "var(--rbw-navy)" }
            }
          >
            {busy ? "Working..." : !overview ? (error ? "Unavailable — tap Retry above" : "Loading...") : clockedIn ? "Clock Out" : "Clock In"}
          </button>
        </div>

        {/* Upcoming shifts */}
        <div
          className="rounded-2xl p-5 shadow-sm"
          style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
        >
          <h2 className="font-bold text-lg mb-4" style={{ color: "var(--rbw-navy)" }}>
            My Shifts
          </h2>
          {!overview ? (
            <p className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>Loading...</p>
          ) : overview.upcoming_shifts.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
              No shifts scheduled in the next three weeks.
            </p>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--rbw-border)" }}>
              {overview.upcoming_shifts.map((s) => (
                <li key={s.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold" style={{ color: "var(--rbw-navy)" }}>
                      {fmtDay(s.scheduled_start)}
                      {isToday(s.scheduled_start) && (
                        <span
                          className="ml-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                          style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
                        >
                          Today
                        </span>
                      )}
                    </div>
                    {s.notes && (
                      <div className="text-xs mt-0.5" style={{ color: "var(--rbw-gray-dark)" }}>{s.notes}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium" style={{ color: "var(--rbw-gray-dark)" }}>
                      {fmtTime(s.scheduled_start)} &ndash; {fmtTime(s.scheduled_end)}
                    </div>
                    {s.status === "swap_requested" ? (
                      <div className="mt-1">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
                          {s.claimed_first_name ? `${s.claimed_first_name} offered to cover` : "Swap requested"}
                        </span>
                        <button
                          onClick={() => handleSwap(s.id, "swap-cancel")}
                          disabled={busy}
                          className="ml-2 text-[11px] font-semibold underline"
                          style={{ color: "var(--rbw-gray-dark)" }}
                        >
                          cancel
                        </button>
                      </div>
                    ) : new Date(s.scheduled_start) > new Date() ? (
                      <button
                        onClick={() => handleSwap(s.id, "swap-request")}
                        disabled={busy}
                        className="mt-1 text-[11px] font-semibold underline"
                        style={{ color: "var(--rbw-blue)" }}
                      >
                        Request swap
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Time off */}
        <div
          className="rounded-2xl p-5 shadow-sm"
          style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg" style={{ color: "var(--rbw-navy)" }}>Time Off</h2>
            <button
              onClick={() => setShowAskOff(!showAskOff)}
              className="px-4 py-2 rounded-xl text-sm font-bold"
              style={{ background: "var(--rbw-navy)", color: "#fff" }}
            >
              {showAskOff ? "Cancel" : "Ask Off"}
            </button>
          </div>

          {showAskOff && (
            <div className="mb-4 p-4 rounded-xl space-y-3" style={{ background: "var(--rbw-bg)" }}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
                    First day off
                  </label>
                  <input
                    type="date"
                    value={offStart}
                    onChange={(e) => {
                      setOffStart(e.target.value);
                      if (!offEnd || offEnd < e.target.value) setOffEnd(e.target.value);
                    }}
                    className="w-full px-3 py-2 rounded-xl outline-none"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
                    Last day off
                  </label>
                  <input
                    type="date"
                    value={offEnd}
                    min={offStart}
                    onChange={(e) => setOffEnd(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl outline-none"
                    style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  />
                </div>
              </div>
              <input
                type="text"
                placeholder="Reason (optional)"
                value={offReason}
                onChange={(e) => setOffReason(e.target.value)}
                className="w-full px-3 py-2 rounded-xl outline-none"
                style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
              />
              <button
                onClick={submitAskOff}
                disabled={askOffBusy || !offStart || !offEnd}
                className="w-full py-3 rounded-xl font-bold disabled:opacity-50"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                {askOffBusy ? "Sending..." : "Send Request"}
              </button>
            </div>
          )}

          {timeOff.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
              No requests yet. Tap Ask Off to request days off — Patrick or Paula will approve it.
            </p>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--rbw-border)" }}>
              {timeOff.map((r) => (
                <li key={r.id} className="py-3 flex items-center justify-between">
                  <span className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
                    {fmtDay(r.start_date)}
                    {r.end_date !== r.start_date && <> &ndash; {fmtDay(r.end_date)}</>}
                    {r.reason && (
                      <span className="block text-xs font-normal" style={{ color: "var(--rbw-gray-dark)" }}>
                        {r.reason}
                      </span>
                    )}
                  </span>
                  <span
                    className="text-[10px] font-bold uppercase px-2 py-1 rounded-full"
                    style={
                      r.status === "approved"
                        ? { background: "#D1FAE5", color: "#065F46" }
                        : r.status === "denied"
                        ? { background: "#FEE2E2", color: "#991B1B" }
                        : { background: "#FEF3C7", color: "#92400E" }
                    }
                  >
                    {r.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
